import { ReactNode, useMemo } from 'react';
import { NavLink } from 'react-router-dom';

import { Box, RedesignDialogsProvider, useTheme } from '@sbv-clients/ui-shared';

import { SideBarMenuItem } from '../sidebar/types';
import { isLinkActive, SETUP_LABEL, useNavModel } from './use-nav-model';

// Groups by link, since page labels can be renamed per facility.
const SETUP_GROUPS: [string, string[]][] = [
  ['Employees', ['/positions', '/departments', '/absence-reason']],
  [
    'Scheduling',
    ['/pro-designations', '/shift-types', '/units', '/opening-templates'],
  ],
  ['Time clocks & apps', ['/clock-communication', '/sbv-pro']],
  ['Organization', ['/company', '/roles', '/imports']],
];

const NavItem = ({ item, on }: { item: SideBarMenuItem; on: boolean }) => {
  const theme = useTheme();
  return (
    <Box
      component={NavLink}
      to={item.link ?? '/'}
      aria-current={on ? 'page' : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        minHeight: 32,
        padding: '6px 9px',
        borderRadius: '7px',
        border: '1px solid transparent',
        fontSize: '13px',
        fontWeight: on ? 600 : 400,
        whiteSpace: 'nowrap',
        textDecoration: 'none',
        color: on ? theme.custom?.brandNavy : theme.custom?.shellMenuItemText,
        background: on ? theme.background.white : 'transparent',
        boxShadow: on ? theme.custom?.shellSetupNavActiveShadow : 'none',
        '&:hover': {
          color: theme.custom?.brandNavy,
          background: on
            ? theme.background.white
            : theme.custom?.shellMenuItemHoverBg,
        },
      }}
    >
      {item.label}
    </Box>
  );
};

// Wraps Setup / Configuration pages with the grouped Setup menu; other
// pages render unchanged.
export const SetupNav = ({
  items,
  children,
}: {
  items: SideBarMenuItem[];
  children: ReactNode;
}) => {
  const theme = useTheme();
  const { setupSection, pathname } = useNavModel(items);

  const groups = useMemo(() => {
    const pages = setupSection?.children ?? [];
    const used = new Set<SideBarMenuItem>();
    const result = SETUP_GROUPS.map(([title, links]) => {
      const groupItems = links
        .map((link) => pages.find((p) => p.link === link))
        .filter((p): p is SideBarMenuItem => !!p);
      groupItems.forEach((p) => used.add(p));
      return { title, items: groupItems };
    });
    const rest = pages.filter((p) => !used.has(p));
    if (rest.length) result.push({ title: 'Other', items: rest });
    return result.filter((g) => g.items.length);
  }, [setupSection]);

  if (!setupSection?.active) return <>{children}</>;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: '12px', md: '20px' },
        height: '100%',
        minHeight: 0,
        padding: { xs: '12px 12px 0', md: '20px 24px 0' },
      }}
    >
      <Box
        component="nav"
        aria-label={SETUP_LABEL}
        sx={{
          flexShrink: 0,
          width: { md: 240 },
          alignSelf: { md: 'flex-start' },
          maxHeight: { md: '100%' },
          overflowY: { md: 'auto' },
          padding: '6px',
          borderRadius: '12px',
          border: `1px solid ${theme.custom?.shellSetupNavBorder}`,
          background: theme.custom?.shellSetupNavBg,
          display: { xs: 'flex', md: 'block' },
          gap: { xs: '2px', md: 0 },
          overflowX: { xs: 'auto', md: 'visible' },
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        <Box
          component="h5"
          sx={{
            display: { xs: 'none', md: 'block' },
            margin: '6px 8px 2px',
            fontSize: '13px',
            fontWeight: 600,
            color: theme.custom?.brandNavy,
          }}
        >
          {SETUP_LABEL}
        </Box>
        <Box
          sx={{
            display: { xs: 'contents', md: 'grid' },
            gap: '1px',
            mt: { md: '6px' },
          }}
        >
          {groups
            .flatMap((group) => group.items)
            .map((item) => (
              <NavItem
                key={item.id ?? item.link}
                item={item}
                on={isLinkActive(item.link, pathname)}
              />
            ))}
        </Box>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, overflow: 'auto' }}>
        {/* Setup pages use the new dialogs, confirms and empty states. */}
        <RedesignDialogsProvider>{children}</RedesignDialogsProvider>
      </Box>
    </Box>
  );
};
